/**
 * Load databases for the connection dialog: the Load button's probe listing and edit mode's auto-load of an already
 * connected connection (moved out of `NewConnectionPopup`, C0).
 *
 * Round B R2: an answer belongs to the snapshot taken at its start — every earlier result goes, a second click, leaving
 * the dialog or a C0 invalidation drops a late answer (the load token), and a list is adopted only while the form still
 * dials what was listed. C0: the list is stored with the target it listed (`targetFingerprint`) in the engine's draft and
 * SHOWN only while the live target matches — editing Server hides it, undoing the edit shows it again.
 */
import { useEffect, useRef, useState } from 'react';
import type { Connection } from '@/types/electron';
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import { connectionToFormValues } from '../../components/connectionToFormValues';
import { getDatabases } from '../../../database-objects/services/databasesService';
import type { ConnectionTestResult, useConnectionTestOutcome } from '../../hooks/useConnectionTestOutcome';
import type { ConnectionForm } from '../state/connectionForm';

type Options = {
  form: ConnectionForm;
  tests: ReturnType<typeof useConnectionTestOutcome>;
  runGate: () => Promise<boolean>;
  probe: (values: ConnectionFormValues) => Promise<{ connection: Connection } | { error: string }>;
  adopt: (databases: string[], values: Partial<ConnectionFormValues>) => void;
  editConnection: Connection | undefined;
  isConnected: boolean;
};

const failure = (error: unknown) => (error instanceof Error ? error.message : 'Unknown error');

export function useLoadDatabases({ form, tests, runGate, probe, adopt, editConnection, isConnected }: Options) {
  const [loading, setLoading] = useState(false);
  const tokenRef = useRef(0);
  useEffect(() => () => { tokenRef.current += 1; }, []);

  const abort = () => {
    tokenRef.current += 1;
    setLoading(false);
  };

  const load = async () => {
    if (form.state.values.engine === 'sqlite') return;
    if (!(await runGate())) return;
    const token = ++tokenRef.current;
    const isCurrent = () => tokenRef.current === token;
    const values = form.state.values;
    const snapshot = tests.snapshotOf(values);
    const show = (result: ConnectionTestResult) => {
      if (isCurrent()) tests.show(snapshot, { result });
    };
    try {
      setLoading(true);
      tests.clear();
      if (!window.electronAPI) throw new Error('Electron API is not available');
      const built = await probe(values);
      if ('error' in built) {
        show({ success: false, variant: 'warning', message: built.error });
        return;
      }
      const connection = built.connection;
      const result = await window.electronAPI.connectToDatabase(connection);
      if (!result.success || !connection.id) {
        show({ success: false, message: result.error || 'Unknown error', connection, errorCode: result.errorCode });
        return;
      }
      try {
        // The dialog lists EVERY database (the only place the limit-to-database flag and database can be changed), and
        // never through the shared listing cache: a per-click probe id with `ignoreConnectionLimit` would leak there.
        const listed = await getDatabases(connection.id, values.engine, { ignoreConnectionLimit: true });
        if (listed.state === 'ok' && listed.databases.length > 0) {
          if (isCurrent() && tests.matchesLive(snapshot)) adopt(listed.databases, values);
          show({ success: true, message: `Loaded ${listed.databases.length} database${listed.databases.length !== 1 ? 's' : ''}` });
        } else if (listed.state === 'empty') {
          // The connect succeeded: a calm warning, never the red "could not connect".
          show({ success: false, variant: 'warning', message: 'Connected successfully, but the server returned no databases — you may not have permission to view them.' });
        } else {
          show({ success: false, variant: 'warning', message: `Connected, but loading databases failed: ${listed.error?.message ?? 'Unknown error'}` });
        }
      } catch (dbError) {
        show({ success: false, variant: 'warning', message: `Connected, but loading databases failed: ${failure(dbError)}` });
      }
      await window.electronAPI.disconnectFromDatabase(connection.id);
    } catch (error) {
      show({ success: false, message: `Error: ${failure(error)}` });
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  // Auto-load when editing a connected connection, at most once per dialog open (a slow pre-save test flaps
  // `isConnected`, and every flap used to restart the spinner). The live connection lists the SAVED configuration, so
  // its list is adopted only while the form still holds exactly that configuration (round B follow-up B3).
  const autoLoadedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isConnected || !editConnection || editConnection.engine === 'sqlite') return;
    if (autoLoadedForRef.current === editConnection.id) return;
    autoLoadedForRef.current = editConnection.id;
    const token = tokenRef.current;
    const saved = connectionToFormValues(editConnection);
    void (async () => {
      try {
        setLoading(true);
        const listed = await getDatabases(editConnection.id, editConnection.engine, { ignoreConnectionLimit: true });
        if (listed.state === 'ok' && listed.databases.length > 0 && tokenRef.current === token && tests.matchesLive(tests.snapshotOf(saved))) {
          adopt(listed.databases, saved);
        }
      } catch (error) {
        console.warn('Failed to auto-load databases:', error);
      } finally {
        if (tokenRef.current === token) setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `tests` is stable; the guard above keys on the connection
  }, [isConnected, editConnection]);

  return { loading, load, abort };
}
